import Edit16 from '@carbon/icons-react/es/edit/16';
import Movement16 from '@carbon/icons-react/es/movement/16';
import OverflowMenuVertical16 from '@carbon/icons-react/es/overflow-menu--vertical/16';
import { Dropdown, Tooltip } from 'antd';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ProjectId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { useProjectSpacesRearrangeModalContext } from 'contexts/providers/rearrange-modal';
import {
  useProjectAsRBACResource,
  useWithAccess,
} from 'hooks/rbac/useWithAccess';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslation } from 'hooks/use-translation';
import { RearrangeDocs } from '../rearrange/modal';

enum MoreActionsKeys {
  Edit = 'Edit',
  Delete = 'Delete',
}

type SpaceMoreActionsTypes = {
  projectId: number;
  spaceId: number;
  handleDelete: (spaceId: number) => Promise<unknown>;
  handleEdit: () => void;
};

const RearrangeWrapper = styled.div``;

export const SpaceMoreActions = ({
  projectId,
  spaceId,
  handleDelete,
  handleEdit,
}: SpaceMoreActionsTypes) => {
  const title = useTranslation('space.delete.confirm.title');
  const content = useTranslation('space.delete.confirm.message');
  const moreActions = useTranslation('more_actions');

  const deleteConfirmation = useDeleteConfirmModal({
    title,
    content,
    onOk: () => handleDelete?.(spaceId),
  });

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [showRearrangeModal, setRearrangeModalVisibility] = useState(false);
  const { setIsRearrangeModalVisible } =
    useProjectSpacesRearrangeModalContext();
  const onRearrange = useCallback(() => {
    setIsRearrangeModalVisible(true);
    setRearrangeModalVisibility(true);
  }, [setIsRearrangeModalVisible]);

  const onClose = useCallback(
    (e: Event) => {
      e.stopPropagation();
      setIsRearrangeModalVisible(false);
      setRearrangeModalVisibility(false);
    },
    [setIsRearrangeModalVisible],
  );
  const projectResource = useProjectAsRBACResource(projectId);
  const { hasAccessToResource } = useWithAccess();
  const hasEditAccess = hasAccessToResource({
    rule: 'SPACE:WRITE',
    resourceData: projectResource,
  });
  const hasDeleteAccess = hasAccessToResource({
    rule: 'SPACE:DELETE',
    resourceData: projectResource,
  });
  const hasRearrangeAccess = hasAccessToResource({
    rule: 'DOCUMENT_REARRANGE:WRITE',
    resourceData: projectResource,
  });

  const SpaceActionsMenu = () => (
    <Menu onClick={(e) => e.domEvent.stopPropagation()}>
      <Menu.Header>
        <FormattedMessage id="more_actions" />
      </Menu.Header>
      {hasEditAccess ? (
        <Menu.Item
          onClick={handleEdit}
          key={MoreActionsKeys.Edit}
          icon={<Edit16 />}
          data-cy={`spaces.${spaceId}.more_actions.rename`}
        >
          <FormattedMessage id="rename" />
        </Menu.Item>
      ) : null}
      {hasRearrangeAccess ? (
        <Menu.Item
          icon={<Movement16 />}
          key="move"
          onClick={onRearrange}
          data-cy={`spaces.${spaceId}.more_actions.rearrange`}
        >
          <FormattedMessage id="templates.more_actions.rearrange" />
        </Menu.Item>
      ) : null}
      {hasDeleteAccess ? (
        <>
          <Menu.Divider />
          <Menu.ItemDelete
            key={MoreActionsKeys.Delete}
            onClick={deleteConfirmation}
            data-cy={`spaces.${spaceId}.more_actions.delete`}
          />
        </>
      ) : null}
    </Menu>
  );

  return hasEditAccess || hasDeleteAccess || hasRearrangeAccess ? (
    <>
      <Tooltip title={moreActions} placement="topRight">
        <Dropdown
          className="more-actions"
          overlay={SpaceActionsMenu}
          trigger={['click']}
          placement="bottomLeft"
        >
          <Button
            type="text"
            icon={<OverflowMenuVertical16 />}
            size="small"
            onClick={handleClick}
            data-cy={`spaces.${spaceId}.more_actions`}
          />
        </Dropdown>
      </Tooltip>

      <RearrangeWrapper onClick={handleClick}>
        <RearrangeDocs
          visible={showRearrangeModal}
          onClose={onClose}
          currentProjectId={projectId as unknown as ProjectId}
        />
      </RearrangeWrapper>
    </>
  ) : null;
};
